import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getVto, normalizeVtoData } from '@/api/eos/vto.api';
import { cn } from '@/lib/cn';

/**
 * Header core-value slot (Stream E, ADR-027 §7.5). Rotates the company's
 * V/TO core values every 12s with a CSS fade. Reads the SHARED
 * `['eos','vto']` query (the same one the V/TO and People Analyzer pages
 * use — single storage, no comms-side copy) and renders null when there
 * are no values, so it costs nothing on orgs that haven't defined any.
 */

const ROTATE_MS = 12_000;

export function CoreValueTicker({ className }: { className?: string }) {
  const vtoQuery = useQuery({
    queryKey: ['eos', 'vto'],
    queryFn: () => getVto(),
    staleTime: 5 * 60_000,
  });

  const coreValues = useMemo(() => {
    const current = vtoQuery.data?.current;
    if (!current) return [];
    return normalizeVtoData(current.dataJson)
      .coreValues.map((v) => v.trim())
      .filter((v) => v !== '');
  }, [vtoQuery.data]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (coreValues.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % coreValues.length);
    }, ROTATE_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [coreValues.length]);

  if (coreValues.length === 0) return null;
  const value = coreValues[index % coreValues.length];

  return (
    <div className={cn('flex min-w-0 items-center gap-1.5 text-[12px] text-kw-muted', className)}>
      <span className="shrink-0 text-[11px] uppercase tracking-wide text-kw-ghost">Core value</span>
      <span className="shrink-0 text-kw-ghost">·</span>
      {/* Re-mount on each rotation so the fade-in animation replays. */}
      <span key={index} className="min-w-0 animate-fade-in truncate italic" title={value}>
        “{value}”
      </span>
    </div>
  );
}
